import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Breadcrumbs } from '@abyss/web/ui/Breadcrumbs';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Constants } from '../../../common/Constants';
import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { getCurrentMember } from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { notMobile } from '../../ConstantsStyles';
import { getFeatureFlag } from '../../Utils';
import {
  BackButton,
  BreadcrumbsContainer,
  HeaderIcon,
} from './Breadcrumb.styled';

interface Props {
  breadcrumbs: any[];
  backgroundColor?: string;
  color?: String;
  currentColor?: String;
  showBreadcrumbAtMediumScreenSize?: boolean;
  showBreadcrumbAtSmallScreenSize?: boolean;
  onBackButtonClick?: () => void;
}

export const Breadcrumb = ({
  breadcrumbs = [],
  backgroundColor = '$bgHeaderColor',
  color = '$interactive1',
  currentColor = '#323334',
  showBreadcrumbAtMediumScreenSize = true,
  onBackButtonClick,
  showBreadcrumbAtSmallScreenSize,
}: Props) => {
  const breadcrumbContainerRef: any = React.createRef();
  const { t } = useTranslation();
  const desktopScreen = useMediaQuery(notMobile);

  const currentMember = getCurrentMember();
  const { navigate } = useRouter();
  const { portalData } = useContext(PortalContext);

  const initialBreadcrumbs = [
    {
      title: t('Find care'),
      href: portalData?.portalConfig?.homePage ?? '/',
    },
  ];
  const setHasBreadCrumbClickedFlag = useStore(
    StoreKeys.SET_BREADCRUMB_CLICKED_FLAG
  );

  const additionalBreadcrumbs = breadcrumbs.map((crumb) => ({
    title: t(crumb.title),
    href: crumb.href,
  }));

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const suppressPsxLinks: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PSX_LINKS_ON_MEMBER_INFO_FAILURE
  );

  const handleBackButton = () => {
    adobeLinkTrackEvent({
      name: 'back button',
      location: 'header',
      type: 'internal',
    });
    if (onBackButtonClick) {
      setHasBreadCrumbClickedFlag(true);
      onBackButtonClick();
    } else {
      navigate(-1);
    }
  };

  const finalBreadcrumbs =
    !currentMember && suppressPsxLinks
      ? additionalBreadcrumbs
      : breadcrumbs.length > 0
      ? initialBreadcrumbs.concat(additionalBreadcrumbs)
      : initialBreadcrumbs;

  useEffect(() => {
    const handleBreadcrumbClick = (elHref, hrefIndx, textContent) => {
      setHasBreadCrumbClickedFlag(true);
      storage.session.remove(
        Constants.STORAGE_KEYS.SESSION.LAST_SELECTED_FROM_DRAWER
      );
      adobeLinkTrackEvent({
        name: textContent || elHref[elHref.length - hrefIndx],
        location: `body:bread crumb`,
        type: 'internal',
        destinationUrl: '',
      });
    };

    const allBreadcrumbLnks = document.querySelectorAll(
      '.abyss-breadcrumb-link'
    );
    allBreadcrumbLnks.forEach((ele) => {
      const elHref = ele.href?.split('/');
      /* This is a wrokaround for Abyss breadcrumbs limitation of not allowing to pass custom data attributes. the name for adobe link tracking is being extracted from href of the breadcrumb link which some times has an encoded string as last endpoint. So below line will decide which part of href need to be considered. If href has encoded string the url segments will be more than 4 in such cases we ignore the last segment. */
      const hrefIndx = elHref.length > 4 ? 2 : 1;
      ele.addEventListener('click', () =>
        handleBreadcrumbClick(elHref, hrefIndx, ele?.textContent)
      );
    });
    breadcrumbContainerRef.current.childNodes[0].ariaLabel = 'breadcrumbs';

    return () => {
      allBreadcrumbLnks.forEach((ele) => {
        ele.removeEventListener('click', () => handleBreadcrumbClick);
      });
    };
  }, []);

  return (
    <React.Fragment>
      <BreadcrumbsContainer
        css={{
          '.abyss-breadcrumbs-root': {
            backgroundColor: backgroundColor
              ? backgroundColor
              : '$bgSubHeaderColor',
            color,
            'li:first-child': {
              'a:first-child': {
                color,
              },
            },
          },
          '.abyss-breadcrumb-link': {
            color,
          },
          '.abyss-breadcrumb-current': {
            color: currentColor,
          },
        }}
        ref={breadcrumbContainerRef}
        showBreadcrumbAtMediumScreenSize={showBreadcrumbAtMediumScreenSize}
        showBreadcrumbAtSmallScreenSize={showBreadcrumbAtSmallScreenSize}
      >
        <Breadcrumbs
          data-auto-testid="nav-container"
          data-testid="nav-container"
          divider="/"
          items={finalBreadcrumbs}
          size={desktopScreen ? '14px' : '$sm'}
          space={desktopScreen ? 14 : 8}
        />
      </BreadcrumbsContainer>
      {breadcrumbs.length ? (
        <BackButton
          className="mobile-nav-back-button"
          color="$primary1"
          data-auto-testid="back-button"
          data-testid="back-button"
          onClick={handleBackButton}
          showBreadcrumbAtMediumScreenSize={showBreadcrumbAtMediumScreenSize}
          showBreadcrumbAtSmallScreenSize={showBreadcrumbAtSmallScreenSize}
          variant={backgroundColor === '#FBFCFE' ? 'ghost' : 'tertiary'}
        >
          <HeaderIcon
            color="$primary1"
            data-auto-testid="back-button"
            data-testid="back-button"
            icon="arrow_back"
            isScreenReadable
            name="back-button"
            size={24}
            title="Back"
          />
        </BackButton>
      ) : (
        ''
      )}
    </React.Fragment>
  );
};
